/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Badge,
    Button,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { toast } from "../../../../utils/toast";
import Plot from "../../../../plotly/Plot";

interface Device {
    UID: string;
    name: string;
    type: string;
}

interface RootState {
    devices: {
        current: Device | null;
    };
}

interface FtaDeviceState {
    online: boolean | null;
    pressure: number | null;
    measurements: string | null;
    diagnostics: string | null;
}

interface FtaDiagnosticsData {
    timestamp: number;
    app: {
        machine_ready: boolean;
        led: string;
        emulator_mode: boolean;
        hid_status: string;
        calibration?: number;
        device_state?: string;
    };
    network: {
        wifi: {
            ip: string | null;
            mac: string | null;
            ssid: string | null;
            signal_dbm: number | null;
        };
        lan: {
            ip: string | null;
            mac: string | null;
        };
    };
    system: {
        cpu: {
            load_1m: number;
            load_5m: number;
            load_15m: number;
            cores: number;
            usage_percent: number;
        };
        memory: {
            total_mb: number;
            used_mb: number;
            available_mb: number;
            usage_percent: number;
        };
        disk: {
            total_gb: number;
            used_gb: number;
            free_gb: number;
            usage_percent: number;
        };
        temperature: Record<string, number>;
        hostname: string;
        uptime_seconds: number;
    };
    usb: {
        devices: Array<{
            bus: string;
            device: string;
            vendor_id: string;
            product_id: string;
            description: string;
        }>;
    };
}

interface ParsedMeasurement {
    distance: number[];
    loadcell: number[];
    pressure: number | null;
}

/**
 * Parse FTA measurement data.
 *
 * Supports two formats:
 *  1. JSON: { pos_arr: number[], force_arr: number[], pressure: number } (new firmware)
 *  2. Legacy string: "dist,forcell#...S#" separated by "l#" (old firmware)
 */
function parseFtaMeasurementData(raw: string): ParsedMeasurement {
    if (!raw || raw.length < 3) {
        return { distance: [], loadcell: [], pressure: null };
    }

    // Try new JSON format first
    if (raw.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed.pos_arr) && Array.isArray(parsed.force_arr)) {
                return {
                    distance: parsed.pos_arr.map(Number),
                    loadcell: parsed.force_arr.map(Number),
                    pressure: typeof parsed.pressure === "number" ? parsed.pressure : null,
                };
            }
        } catch {
            // fall through to legacy parsing
        }
    }

    // Legacy string format
    const distance: number[] = [];
    const loadcell: number[] = [];
    let pressure: number | null = null;

    // Remove # and whitespace, split on 'l'
    const cleaned = raw.replace(/#/g, "").replace(/\n/g, "").replace(/\r/g, "").trim();
    const parts = cleaned.split("l");

    // Check if last part contains 'S' (pressure reading)
    if (parts.length > 0 && parts[parts.length - 1].includes("S")) {
        const lastPart = parts.pop()!;
        const pressureStr = lastPart.split("S")[0];
        const parsedPressure = parseFloat(pressureStr);
        if (!Number.isNaN(parsedPressure)) {
            pressure = parsedPressure;
        }
    }

    // Parse remaining data lines
    parts
        .filter((part) => part.trim() !== "")
        .forEach((part) => {
            const values = part.split(",");
            if (values.length !== 2) return;

            const d = parseFloat(values[0]);
            const l = parseFloat(values[1]);

            if (Number.isNaN(d) || Number.isNaN(l)) return;

            distance.push(d);
            loadcell.push(l);
        });

    return { distance, loadcell, pressure };
}

/**
 * FTA Device Controls Component
 * Provides MQTT topic-based controls for FTA devices with real-time polling updates
 */
export function FtaControls() {
    const device = useSelector((state: RootState) => state.devices.current);
    const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
    const [deviceState, setDeviceState] = useState<FtaDeviceState>({
        online: null,
        pressure: null,
        measurements: null,
        diagnostics: null,
    });

    // Form state
    const [triggerEventId, setTriggerEventId] = useState<string>("");
    const [isReady, setIsReady] = useState<boolean>(false);
    const [diagnosticsData, setDiagnosticsData] = useState<FtaDiagnosticsData | null>(null);
    const [parsedMeasurement, setParsedMeasurement] = useState<ParsedMeasurement | null>(null);

    const fetchDeviceState = useCallback(async () => {
        if (!device?.UID) return;

        setIsLoadingState(true);
        try {
            const response = await callAvosApi(`/device/${device.UID}/state/stream`);
            setDeviceState(response.data as unknown as FtaDeviceState);
        } catch (error) {
            toast.warning("Failed to fetch device state");
        } finally {
            setIsLoadingState(false);
        }
    }, [device?.UID]);

    // Poll device state updates every 2 seconds
    useEffect(() => {
        if (!device || device.type !== "fta") {
            return undefined;
        }

        let isMounted = true;
        let pollInterval: NodeJS.Timeout | null = null;

        const startPolling = async () => {
            await fetchDeviceState();

            pollInterval = setInterval(async () => {
                if (!isMounted) return;

                try {
                    const response = await callAvosApi(`/device/${device.UID}/state/stream`);
                    const state = response.data as unknown as FtaDeviceState;
                    setDeviceState(state);

                    // Parse diagnostics from state
                    if (state.diagnostics) {
                        try {
                            const diag = JSON.parse(state.diagnostics);
                            setDiagnosticsData(diag);

                            // device_state "unlocked" means machine is ready/unlocked
                            // fall back to machine_ready if device_state not present
                            if (diag.app?.device_state !== undefined) {
                                setIsReady(diag.app.device_state === "unlocked");
                            } else if (diag.app?.machine_ready !== undefined) {
                                setIsReady(diag.app.machine_ready);
                            }
                        } catch (e) {
                            // ignore parse errors
                        }
                    }

                    // Parse measurement data from state
                    if (state.measurements) {
                        const parsed = parseFtaMeasurementData(state.measurements);
                        if (parsed.distance.length > 0) {
                            setParsedMeasurement(parsed);
                        }
                    }
                } catch (error) {
                    // Silently fail - don't show error for polling failures
                }
            }, 2000);
        };

        startPolling();

        return () => {
            isMounted = false;
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [device?.UID, fetchDeviceState]);

    const publishMqtt = useCallback(async (topic: string, payload = "") => {
        if (!device?.UID) return;

        try {
            await callAvosApi(`/device/${device.UID}/mqtt/publish`, {
                method: "POST",
                data: { topic, payload: String(payload) },
            });
            toast.success(`Published to ${topic} ${payload ?? ""}`);
        } catch (error: any) {
            const errorMsg = error.response?.data?.detail || error.message;
            toast.error(`Error: ${errorMsg}`);
        }
    }, [device?.UID]);

    // FTA MQTT topics use the device label (name without "fta-" prefix), not the UUID
    const ftaMqttId = device?.name?.replace(/^fta-/, "") ?? "";

    const handleSetReady = useCallback(() => {
        publishMqtt(`fta/${ftaMqttId}/exec`, "r");
    }, [publishMqtt, ftaMqttId]);

    const handleSetUnready = useCallback(() => {
        publishMqtt(`fta/${ftaMqttId}/exec`, "o");
    }, [publishMqtt, ftaMqttId]);

    const handleTrigger = useCallback(async () => {
        await publishMqtt(`fta/${ftaMqttId}/trigger`, triggerEventId || "1");
    }, [publishMqtt, ftaMqttId, triggerEventId]);

    const handleRefreshState = useCallback(() => {
        fetchDeviceState();
    }, [fetchDeviceState]);

    const getConnectionBadge = (): JSX.Element => {
        if (isLoadingState) {
            return <Badge color="info" className="ms-2">Loading...</Badge>;
        }
        // online=1 (true) means device just restarted, online=0 (false) means running normally
        if (deviceState.online === false) {
            return <Badge color="success" className="ms-2">Online</Badge>;
        }
        if (deviceState.online === true) {
            return <Badge color="warning" className="ms-2">Restarted</Badge>;
        }
        return <Badge color="secondary" className="ms-2">Unknown</Badge>;
    };

    // Plotly chart data for measurement
    const plotData = useMemo(() => {
        if (!parsedMeasurement || parsedMeasurement.distance.length === 0) return null;

        return [
            {
                x: parsedMeasurement.distance,
                y: parsedMeasurement.loadcell,
                type: "scatter" as const,
                mode: "lines" as const,
                name: "Load Cell (gram)",
                line: { color: "#1f77b4", width: 2 },
            },
        ];
    }, [parsedMeasurement]);

    const plotLayout = useMemo(() => {
        const pressureText = parsedMeasurement?.pressure != null
            ? ` | Pressure: ${parsedMeasurement.pressure.toFixed(3)} kg`
            : "";
        return {
            title: `FTA Measurement${pressureText}`,
            xaxis: { title: "Distance travelled (stepsize)" },
            yaxis: { title: "Loadcell output (g)" },
            autosize: true,
            margin: { l: 60, r: 30, t: 50, b: 50 },
            height: 400,
        };
    }, [parsedMeasurement]);

    const formatUptime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    };

    // Progress bar color based on percentage
    const progressColor = (pct: number) => {
        if (pct < 60) return "#28a745";
        if (pct < 80) return "#fd7e14";
        return "#dc3545";
    };

    // WiFi signal: map dBm to 0-4 bars + color + label
    const wifiSignal = (dbm: number | null): { bars: number; color: string; label: string } => {
        if (dbm == null) return { bars: 0, color: "#adb5bd", label: "No signal" };
        if (dbm >= -55) return { bars: 4, color: "#28a745", label: "Excellent" };
        if (dbm >= -65) return { bars: 3, color: "#28a745", label: "Good" };
        if (dbm >= -75) return { bars: 2, color: "#fd7e14", label: "Fair" };
        if (dbm >= -85) return { bars: 1, color: "#dc3545", label: "Weak" };
        return { bars: 0, color: "#adb5bd", label: "No signal" };
    };

    // Temperature chip color
    const tempColor = (c: number) => {
        if (c < 50) return "#28a745";
        if (c < 70) return "#fd7e14";
        return "#dc3545";
    };

    if (!device || device.type !== "fta") {
        return <Alert color="warning">This device is not an FTA device.</Alert>;
    }

    return (
        <div className="pt-3">

            {/* Device Controls */}
            <section className="mb-4">
                <h5 className="mb-3">
                    Device Controls
                    {getConnectionBadge()}
                </h5>
                <div css={css`
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 0.375rem;
                    padding: 1rem 1.25rem;
                `}>
                    <Row className="align-items-center g-3">
                        <Col md={5}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Button color="success" onClick={handleSetReady} size="sm">
                                    Unlock device
                                </Button>
                                <Button color="danger" onClick={handleSetUnready} size="sm">
                                    Lock device
                                </Button>
                                <Badge
                                    css={css`font-size: 0.8rem; padding: 0.35em 0.65em;`}
                                    color={isReady ? "success" : "secondary"}
                                >
                                    {isReady ? "Unlocked" : "Locked"}
                                </Badge>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div css={css`font-size: 0.75rem; color: #6c757d; text-transform: uppercase; letter-spacing: 0.04em;`}>Last Pressure</div>
                            {deviceState.pressure != null ? (
                                <div>
                                    <span css={css`font-size: 1.6rem; font-weight: 700; line-height: 1.1;`}>
                                        {deviceState.pressure.toFixed(3)}
                                    </span>
                                    <span css={css`font-size: 0.85rem; color: #6c757d; margin-left: 4px;`}>kg</span>
                                </div>
                            ) : (
                                <span css={css`color: #adb5bd; font-size: 0.9rem;`}>—</span>
                            )}
                        </Col>
                        <Col md={4}>
                            <FormGroup className="mb-1">
                                <Label css={css`font-size: 0.8rem; margin-bottom: 2px;`}>Event ID (optional)</Label>
                                <Input
                                    bsSize="sm"
                                    type="text"
                                    placeholder="Leave empty for standalone"
                                    value={triggerEventId}
                                    onChange={(e) => setTriggerEventId(e.target.value)}
                                />
                            </FormGroup>
                            <Button color="primary" size="sm" onClick={handleTrigger}>
                                ▶ Trigger Measurement
                            </Button>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Measurement Plot */}
            <section className="mb-4">
                <h5 className="mb-2">Latest Measurement</h5>
                {plotData ? (
                    <div css={css`
                        background: #fff;
                        border: 1px solid #dee2e6;
                        border-radius: 0.375rem;
                        padding: 0.5rem;
                    `}>
                        <Plot
                            data={plotData}
                            layout={plotLayout}
                            config={{ responsive: true, displayModeBar: true }}
                            style={{ width: "100%", height: "400px" }}
                        />
                    </div>
                ) : (
                    <div css={css`
                        background: #f8f9fa;
                        border: 1px solid #dee2e6;
                        border-radius: 0.375rem;
                        padding: 1.5rem;
                        text-align: center;
                        color: #6c757d;
                        font-size: 0.9rem;
                    `}>
                        No measurement data yet — trigger a measurement or wait for the device to send data.
                    </div>
                )}
                <div className="mt-1">
                    <Button color="outline-secondary" size="sm" onClick={handleRefreshState}>
                        ↺ Refresh
                    </Button>
                </div>
            </section>

            {/* Diagnostics */}
            {diagnosticsData && (
                <section className="mb-4">
                    <h5 className="mb-2">
                        Diagnostics
                        <small css={css`font-weight: 400; font-size: 0.75rem; color: #6c757d; margin-left: 8px;`}>
                            {diagnosticsData.timestamp ? new Date(diagnosticsData.timestamp * 1000).toLocaleString() : ""}
                        </small>
                    </h5>

                    {/* App status strip */}
                    <div css={css`
                        background: #fff;
                        border: 1px solid #dee2e6;
                        border-radius: 0.375rem;
                        padding: 0.875rem 1.25rem;
                        margin-bottom: 0.5rem;
                        display: flex;
                        flex-wrap: wrap;
                        gap: 1rem;
                        align-items: center;
                    `}>
                        {/* Device state pill */}
                        <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 80px;`}>
                            <span css={css`
                                display: inline-block;
                                padding: 0.3em 0.75em;
                                border-radius: 999px;
                                font-size: 0.82rem;
                                font-weight: 600;
                                background: ${isReady ? "#d1fae5" : "#fee2e2"};
                                color: ${isReady ? "#065f46" : "#991b1b"};
                                border: 1px solid ${isReady ? "#6ee7b7" : "#fca5a5"};
                            `}>
                                {diagnosticsData.app.device_state ?? (isReady ? "unlocked" : "locked")}
                            </span>
                            <span css={css`font-size: 0.68rem; color: #9ca3af; margin-top: 2px;`}>state</span>
                        </div>

                        {/* HID */}
                        {(() => {
                            const hidStatus = diagnosticsData.app.hid_status;
                            let hidBg = "#f3f4f6";
                            let hidColor = "#374151";
                            let hidBorder = "#d1d5db";
                            if (hidStatus === "connected") { hidBg = "#d1fae5"; hidColor = "#065f46"; hidBorder = "#6ee7b7"; } else if (hidStatus === "emulating") { hidBg = "#dbeafe"; hidColor = "#1e40af"; hidBorder = "#93c5fd"; }
                            return (
                                <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 80px;`}>
                                    <span css={css`
                                        display: inline-block;
                                        padding: 0.3em 0.75em;
                                        border-radius: 999px;
                                        font-size: 0.82rem;
                                        font-weight: 600;
                                        background: ${hidBg};
                                        color: ${hidColor};
                                        border: 1px solid ${hidBorder};
                                    `}>
                                        {hidStatus}
                                    </span>
                                    <span css={css`font-size: 0.68rem; color: #9ca3af; margin-top: 2px;`}>HID</span>
                                </div>
                            );
                        })()}

                        {/* Emulator mode */}
                        {diagnosticsData.app.emulator_mode && (
                            <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 80px;`}>
                                <span css={css`
                                    display: inline-block;
                                    padding: 0.3em 0.75em;
                                    border-radius: 999px;
                                    font-size: 0.82rem;
                                    font-weight: 600;
                                    background: #fef3c7;
                                    color: #92400e;
                                    border: 1px solid #fcd34d;
                                `}>
                                    emulator
                                </span>
                                <span css={css`font-size: 0.68rem; color: #9ca3af; margin-top: 2px;`}>mode</span>
                            </div>
                        )}

                        {/* Calibration */}
                        {diagnosticsData.app.calibration != null && (
                            <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 70px;`}>
                                <span css={css`font-size: 1rem; font-weight: 700;`}>{diagnosticsData.app.calibration.toFixed(1)}</span>
                                <span css={css`font-size: 0.68rem; color: #9ca3af;`}>calibration</span>
                            </div>
                        )}

                        {/* Hostname */}
                        <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 80px;`}>
                            <span css={css`font-size: 0.9rem; font-weight: 600;`}>{diagnosticsData.system.hostname}</span>
                            <span css={css`font-size: 0.68rem; color: #9ca3af;`}>hostname</span>
                        </div>

                        {/* Uptime */}
                        <div css={css`display: flex; flex-direction: column; align-items: center; min-width: 70px;`}>
                            <span css={css`font-size: 0.9rem; font-weight: 600;`}>{formatUptime(diagnosticsData.system.uptime_seconds)}</span>
                            <span css={css`font-size: 0.68rem; color: #9ca3af;`}>uptime</span>
                        </div>
                    </div>

                    {/* Resource bars */}
                    <Row className="g-2 mb-2">
                        {/* CPU */}
                        <Col md={4}>
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;`}>
                                <div className="d-flex justify-content-between align-items-baseline mb-1">
                                    <span css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;`}>CPU</span>
                                    <span css={css`font-size:0.85rem;font-weight:700;color:${progressColor(diagnosticsData.system.cpu.usage_percent)};`}>
                                        {diagnosticsData.system.cpu.usage_percent.toFixed(1)}%
                                    </span>
                                </div>
                                <div css={css`height:8px;background:#e9ecef;border-radius:999px;overflow:hidden;`}>
                                    <div css={css`height:100%;width:${Math.min(diagnosticsData.system.cpu.usage_percent, 100)}%;background:${progressColor(diagnosticsData.system.cpu.usage_percent)};border-radius:999px;transition:width 0.4s ease;`} />
                                </div>
                                <div css={css`font-size:0.68rem;color:#9ca3af;margin-top:4px;`}>
                                    {diagnosticsData.system.cpu.cores} cores · load {diagnosticsData.system.cpu.load_1m.toFixed(2)} / {diagnosticsData.system.cpu.load_5m.toFixed(2)} / {diagnosticsData.system.cpu.load_15m.toFixed(2)}
                                </div>
                            </div>
                        </Col>

                        {/* Memory */}
                        <Col md={4}>
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;`}>
                                <div className="d-flex justify-content-between align-items-baseline mb-1">
                                    <span css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;`}>Memory</span>
                                    <span css={css`font-size:0.85rem;font-weight:700;color:${progressColor(diagnosticsData.system.memory.usage_percent)};`}>
                                        {diagnosticsData.system.memory.usage_percent.toFixed(1)}%
                                    </span>
                                </div>
                                <div css={css`height:8px;background:#e9ecef;border-radius:999px;overflow:hidden;`}>
                                    <div css={css`height:100%;width:${Math.min(diagnosticsData.system.memory.usage_percent, 100)}%;background:${progressColor(diagnosticsData.system.memory.usage_percent)};border-radius:999px;transition:width 0.4s ease;`} />
                                </div>
                                <div css={css`font-size:0.68rem;color:#9ca3af;margin-top:4px;`}>
                                    {(diagnosticsData.system.memory.used_mb / 1024).toFixed(1)} GB used of {(diagnosticsData.system.memory.total_mb / 1024).toFixed(1)} GB
                                </div>
                            </div>
                        </Col>

                        {/* Disk */}
                        <Col md={4}>
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;`}>
                                <div className="d-flex justify-content-between align-items-baseline mb-1">
                                    <span css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;`}>Disk</span>
                                    <span css={css`font-size:0.85rem;font-weight:700;color:${progressColor(diagnosticsData.system.disk.usage_percent)};`}>
                                        {diagnosticsData.system.disk.usage_percent.toFixed(1)}%
                                    </span>
                                </div>
                                <div css={css`height:8px;background:#e9ecef;border-radius:999px;overflow:hidden;`}>
                                    <div css={css`height:100%;width:${Math.min(diagnosticsData.system.disk.usage_percent, 100)}%;background:${progressColor(diagnosticsData.system.disk.usage_percent)};border-radius:999px;transition:width 0.4s ease;`} />
                                </div>
                                <div css={css`font-size:0.68rem;color:#9ca3af;margin-top:4px;`}>
                                    {diagnosticsData.system.disk.used_gb.toFixed(1)} GB used of {diagnosticsData.system.disk.total_gb.toFixed(1)} GB
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Network + Temperatures */}
                    <Row className="g-2 mb-2">
                        <Col md={6}>
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;height:100%;`}>
                                <div css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:0.5rem;`}>Network</div>
                                <div className="d-flex align-items-center gap-3">
                                    {/* Signal bars */}
                                    {(() => {
                                        const sig = wifiSignal(diagnosticsData.network.wifi.signal_dbm);
                                        return (
                                            <div css={css`display:flex;flex-direction:column;align-items:center;`}>
                                                <div css={css`display:flex;align-items:flex-end;gap:2px;height:20px;`}>
                                                    {[1, 2, 3, 4].map((b) => (
                                                        <div key={b} css={css`
                                                            width:5px;
                                                            height:${4 + b * 4}px;
                                                            border-radius:2px;
                                                            background:${sig.bars >= b ? sig.color : "#dee2e6"};
                                                        `} />
                                                    ))}
                                                </div>
                                                <span css={css`font-size:0.65rem;color:${sig.color};margin-top:2px;font-weight:600;`}>{sig.label}</span>
                                                <span css={css`font-size:0.63rem;color:#9ca3af;`}>
                                                    {diagnosticsData.network.wifi.signal_dbm != null ? `${diagnosticsData.network.wifi.signal_dbm} dBm` : ""}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <div css={css`font-size:0.82rem;line-height:1.6;`}>
                                        <div><span css={css`color:#9ca3af;`}>WiFi</span>&nbsp;{diagnosticsData.network.wifi.ip || "N/A"}</div>
                                        <div><span css={css`color:#9ca3af;`}>SSID</span>&nbsp;{diagnosticsData.network.wifi.ssid || "N/A"}</div>
                                        <div css={css`font-size:0.68rem;color:#9ca3af;`}>{diagnosticsData.network.wifi.mac || ""}</div>
                                        {diagnosticsData.network.lan.ip && (
                                            <div><span css={css`color:#9ca3af;`}>LAN</span>&nbsp;{diagnosticsData.network.lan.ip}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col md={6}>
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;height:100%;`}>
                                <div css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:0.5rem;`}>Temperatures</div>
                                <div css={css`display:flex;flex-wrap:wrap;gap:0.4rem;`}>
                                    {Object.entries(diagnosticsData.system.temperature).map(([sensor, temp]) => (
                                        <div key={sensor} css={css`
                                            display:flex;flex-direction:column;align-items:center;
                                            padding:0.25rem 0.5rem;border-radius:0.25rem;
                                            background:${tempColor(temp)}18;
                                            border:1px solid ${tempColor(temp)}55;
                                            min-width:62px;
                                        `}>
                                            <span css={css`font-size:0.95rem;font-weight:700;color:${tempColor(temp)};`}>{temp.toFixed(0)}°C</span>
                                            <span css={css`font-size:0.63rem;color:#6b7280;text-align:center;line-height:1.2;margin-top:1px;`}>
                                                {sensor.replace("-thermal", "")}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* USB devices */}
                    {(() => {
                        const visibleUsb = diagnosticsData.usb.devices.filter((usb) => !usb.description.toLowerCase().includes("linux foundation"));
                        if (visibleUsb.length === 0) return null;
                        return (
                            <div css={css`background:#fff;border:1px solid #dee2e6;border-radius:0.375rem;padding:0.75rem 1rem;`}>
                                <div css={css`font-size:0.75rem;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:0.4rem;`}>
                                    USB Devices ({visibleUsb.length})
                                </div>
                                <div css={css`display:flex;flex-wrap:wrap;gap:0.35rem;`}>
                                    {visibleUsb.map((usb, idx) => {
                                        const isFta = usb.description.toLowerCase().includes("fruit") || usb.description.toLowerCase().includes("texture");
                                        return (
                                            <div key={idx} css={css`
                                            font-size:0.75rem;
                                            padding:0.2rem 0.5rem;
                                            border-radius:0.25rem;
                                            background:${isFta ? "#dbeafe" : "#f3f4f6"};
                                            border:1px solid ${isFta ? "#93c5fd" : "#e5e7eb"};
                                            color:${isFta ? "#1e40af" : "#374151"};
                                            font-weight:${isFta ? "600" : "400"};
                                        `}>
                                                {isFta ? "⭐ " : ""}{usb.description}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </section>
            )}

            {/* Help */}
            <section className="mb-4">
                <Alert color="info">
                    <h6>FTA MQTT Topics</h6>
                    <ul className="mb-0" css={css`font-size: 0.85rem;`}>
                        <li><code>fta/{ftaMqttId}/exec</code> — <code>r</code> = unlock device, <code>o</code> = lock device</li>
                        <li><code>fta/{ftaMqttId}/trigger</code> — start measurement (payload = event_id or <code>1</code>)</li>
                        <li><code>fta/{ftaMqttId}/online</code> — 0 = online, 1 = just restarted</li>
                        <li><code>fta/{ftaMqttId}/pressure</code> — current load cell reading (kg)</li>
                        <li><code>fta/{ftaMqttId}/measurements</code> — raw measurement data (distance + load cell)</li>
                        <li><code>fta/{ftaMqttId}/diagnostics</code> — JSON: system, network, app status</li>
                    </ul>
                </Alert>
            </section>
        </div>
    );
}
